import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'

import { createQueryKey } from '@/lib/react-query'
import { camelToWords } from '@/utils/stringHelper'

export const useCrud = (resource, service) => {
  const queryClient = useQueryClient()

  const queryKey = createQueryKey(resource)

  const useFetchAll = (parameter, listType = '', options = {}) => {
    const { page, pageSize, sortBy = '', orderBy = 1, filters = {} } = parameter
    return useQuery({
      queryKey: listType === '' ? queryKey.list(parameter) : [...queryKey.list(parameter), listType],
      queryFn: () => service.fetchAll(parameter, listType),
      placeholderData: keepPreviousData,
      ...options,
    })
  }

  const useCount = (parameter = {}, listType = '') => {
    const { page = 1, pageSize = 0, sortBy = '', orderBy = 1, filters = {} } = parameter
    return useQuery({
      queryKey: listType === '' ? queryKey.list(parameter) : [...queryKey.list(parameter), listType],
      queryFn: () => service.fetchAll(parameter, listType),
      placeholderData: keepPreviousData,
      select: (data) => data?.count ?? 0,
    })
  }

  const useFetchOne = (id) => {
    return useQuery({
      queryKey: queryKey.detail(id),
      queryFn: () => service.fetchOne(id),
      enabled: !!id && id !== '',
    })
  }

  // Create a new resource
  const useCreate = () => {
    return useMutation({
      mutationFn: (data) => service.create(data),
      onSuccess: () => {
        toast.info(`Successfully Create ${camelToWords(resource)}`)
        queryClient.invalidateQueries(queryKey.lists())
      },
    })
  }

  // Update an existing resource
  const useUpdate = () => {
    return useMutation({
      mutationFn: ({ id, data }) => service.update(id, data),
      onSuccess: (res) => {
        toast.info(`Successfully Update ${camelToWords(resource)}`)
        queryClient.invalidateQueries(queryKey.lists())
        if (res?.data?._id) {
          queryClient.invalidateQueries(queryKey.detail(res?.data?._id))
        }
      },
    })
  }

  // Delete a resource
  const useRemove = () => {
    return useMutation({
      mutationFn: (id) => service.remove(id),
      onSuccess: () => {
        toast.info(`Successfully Remove ${camelToWords(resource)}`)
        queryClient.invalidateQueries(queryKey.lists())
      },
    })
  }

  return {
    queryKey,
    useFetchAll,
    useFetchOne,
    useCreate,
    useUpdate,
    useRemove,
    useCount,
  }
}
